import React, { useMemo, useState } from "react"
import { DialogView } from "../../../components/DialogView"
import {
    previewAdjustLineQuantities,
    applyAdjustLineQuantities,
} from "../../../services/orderServices"

// Self-service quantity adjustment for REM orders.
// Lower order_line quantities and send the excess stock units to LOST.
// Backend enforces all pre-conditions; this UI surfaces them and the diff.
export const AdjustQuantitiesDialog = ({ open, setOpen, order, onApplied }) => {
    const initialAdjustments = useMemo(
        () =>
            (order?.order_lines || []).map((ol) => ({
                order_line_id: ol.id,
                sku_or_upc: ol.product?.bar_code || ol.product?.product_codes?.[0]?.sku || ol.sku || "",
                product_name: ol.product?.name || ol.name || "",
                from_quantity: ol.quantity,
                new_quantity: ol.quantity,
            })),
        [order]
    )

    const [adjustments, setAdjustments] = useState(initialAdjustments)
    const [reason, setReason] = useState("")
    const [requestedByAccount, setRequestedByAccount] = useState("")
    const [previewResult, setPreviewResult] = useState(null)
    const [isPreviewing, setIsPreviewing] = useState(false)
    const [isApplying, setIsApplying] = useState(false)
    const [errorBody, setErrorBody] = useState(null)

    const wasOpen = React.useRef(false)
    React.useEffect(() => {
        if (open && !wasOpen.current) {
            setAdjustments(initialAdjustments)
            setReason("")
            setRequestedByAccount("")
            setPreviewResult(null)
            setErrorBody(null)
        }
        wasOpen.current = open
    }, [open, initialAdjustments])

    // Treat empty input as "unchanged" so clearing the field doesn't silently
    // become "drop to 0". Backend rejects raises, but a typo'd clear shouldn't
    // implicitly mean "send everything to LOST".
    const isFilled = (v) => v !== "" && v !== null && v !== undefined && Number.isFinite(Number(v))
    const changedAdjustments = adjustments.filter(
        (a) => isFilled(a.new_quantity) && Number(a.new_quantity) !== a.from_quantity
    )
    // Block client-side raises so the preview button isn't wasted on a 422.
    const hasInvalidRaise = adjustments.some(
        (a) => isFilled(a.new_quantity) && Number(a.new_quantity) > a.from_quantity
    )
    const hasNegativeQuantity = adjustments.some(
        (a) => isFilled(a.new_quantity) && Number(a.new_quantity) < 0
    )
    // The backend coerces with Integer(), so 2.5 would silently become 2 and send one more unit to LOST.
    const hasFractionalQuantity = adjustments.some(
        (a) => isFilled(a.new_quantity) && !Number.isInteger(Number(a.new_quantity))
    )

    const payloadAdjustments = changedAdjustments.map((a) => ({
        sku_or_upc: a.sku_or_upc,
        new_quantity: Number(a.new_quantity),
    }))

    const updateQuantity = (orderLineId, value) => {
        setAdjustments((prev) =>
            prev.map((a) =>
                a.order_line_id === orderLineId ? { ...a, new_quantity: value } : a
            )
        )
        setPreviewResult(null)
        setErrorBody(null)
    }

    const allPreconditionsOk =
        previewResult?.preconditions?.length > 0 && previewResult.preconditions.every((p) => p.ok)
    const canPreview =
        payloadAdjustments.length > 0 &&
        !hasInvalidRaise &&
        !hasNegativeQuantity &&
        !hasFractionalQuantity &&
        !isPreviewing &&
        !isApplying
    const canApply =
        previewResult &&
        allPreconditionsOk &&
        reason.trim().length > 0 &&
        requestedByAccount.trim().length > 0 &&
        !isPreviewing &&
        !isApplying

    const handlePreview = async () => {
        setErrorBody(null)
        setIsPreviewing(true)
        try {
            const result = await previewAdjustLineQuantities(order.id, payloadAdjustments)
            setPreviewResult(result)
        } catch (err) {
            const body = err?.response?.data
            // A blocked preview answers 422 with the success shape and no `error`: the failing
            // preconditions below already explain it, so no empty banner.
            if (body?.status !== "preview_blocked") setErrorBody(body || { error: err?.message || "Error desconocido" })
            setPreviewResult(body?.preconditions?.length > 0 ? body : null)
        } finally {
            setIsPreviewing(false)
        }
    }

    const handleApply = async () => {
        setErrorBody(null)
        setIsApplying(true)
        try {
            const result = await applyAdjustLineQuantities(order.id, {
                adjustments: payloadAdjustments,
                reason: reason.trim(),
                requested_by_account: requestedByAccount.trim(),
            })
            if (onApplied) onApplied(result)
            setOpen(false)
        } catch (err) {
            const body = err?.response?.data
            setErrorBody(body || { error: err?.message || "Error desconocido" })
            // Backend re-runs preconditions under @order.with_lock, so a 422
            // here can mean state changed concurrently (e.g. a picker just
            // PICKed a RESERVED unit). Refresh the preview pane so the user
            // sees the now-failing precondition instead of a stale ✓ list.
            setPreviewResult(body?.preconditions?.length > 0 ? body : null)
        } finally {
            setIsApplying(false)
        }
    }

    if (!order) return null

    return (
        <DialogView open={open} setOpen={setOpen}>
            <div className="w-full">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">
                    Ajustar cantidades — Pedido #{order.order_number}
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                    Baja la cantidad de uno o varios SKUs. Las piezas excedentes se moverán
                    a la ubicación <span className="font-mono">LF-1-1-1</span> (LOST).
                    Para subir cantidades contactar a Tech.
                </p>

                <div className="border border-gray-200 rounded-md divide-y divide-gray-200 mb-4">
                    {adjustments.map((adj) => (
                        <div
                            key={adj.order_line_id}
                            className="px-3 py-2 flex items-center justify-between"
                        >
                            <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                    {adj.product_name}
                                </div>
                                <div className="text-xs text-gray-500 font-mono">
                                    {adj.sku_or_upc}
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <span className="text-xs text-gray-500">
                                    Actual: <span className="font-semibold">{adj.from_quantity}</span>
                                </span>
                                <label className="text-xs text-gray-500">Nueva:</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={adj.from_quantity}
                                    step={1}
                                    value={adj.new_quantity}
                                    disabled={!adj.sku_or_upc}
                                    title={adj.sku_or_upc ? undefined : "Producto sin código: no se puede ajustar"}
                                    onChange={(e) => updateQuantity(adj.order_line_id, e.target.value)}
                                    className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Motivo (obligatorio)
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="Ej. Pieza no localizada durante picking"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Cuenta solicitante (obligatorio)
                        </label>
                        <input
                            type="text"
                            value={requestedByAccount}
                            onChange={(e) => setRequestedByAccount(e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="Ej. Nuestro Secreto"
                        />
                    </div>
                </div>

                {previewResult && (
                    <div className="mb-4 border border-gray-200 rounded p-3 bg-gray-50">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                            Pre-condiciones
                        </h3>
                        <ul className="space-y-1 text-xs">
                            {previewResult.preconditions?.map((p) => (
                                <li key={p.code} className="flex items-start">
                                    <span className={p.ok ? "text-green-600" : "text-red-600"}>
                                        {p.ok ? "✓" : "✗"}
                                    </span>
                                    <span className="ml-2 font-mono text-gray-500">{p.code}</span>
                                    {!p.ok && p.message && (
                                        <span className="ml-2 text-red-700">{p.message}</span>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {previewResult.stock_unit_changes?.length > 0 && (
                            <div className="mt-3">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Unidades a mover a LOST ({previewResult.stock_unit_changes.length})
                                </h3>
                                <ul className="space-y-0.5 text-xs font-mono text-gray-600 max-h-32 overflow-y-auto">
                                    {previewResult.stock_unit_changes.map((c) => (
                                        <li key={c.stock_unit_id}>
                                            #{c.stock_unit_id} — {c.from_status} @ {c.from_location || "—"}
                                            {" → "}
                                            LOST @ {c.to_location}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {(hasInvalidRaise || hasNegativeQuantity || hasFractionalQuantity) && (
                    <div className="mb-4 border border-amber-200 rounded p-3 bg-amber-50 text-sm text-amber-800">
                        {hasInvalidRaise && (
                            <div>Solo se permite bajar cantidades. Para subir cantidades contactar a Tech.</div>
                        )}
                        {hasNegativeQuantity && (
                            <div>La nueva cantidad no puede ser negativa.</div>
                        )}
                        {hasFractionalQuantity && (
                            <div>La nueva cantidad debe ser un número entero.</div>
                        )}
                    </div>
                )}

                {errorBody && (
                    <div className="mb-4 border border-red-200 rounded p-3 bg-red-50 text-sm text-red-800">
                        <div className="font-semibold">{errorBody.error || "Error"}</div>
                        {errorBody.code && (
                            <div className="text-xs font-mono mt-1">code: {errorBody.code}</div>
                        )}
                    </div>
                )}

                <div className="flex justify-end space-x-2 mt-2">
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        disabled={isApplying}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handlePreview}
                        disabled={!canPreview}
                        className="px-3 py-1.5 text-sm border border-indigo-500 rounded text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                    >
                        {isPreviewing ? "Calculando..." : "Vista previa"}
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={!canApply}
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {isApplying ? "Aplicando..." : "Confirmar"}
                    </button>
                </div>
            </div>
        </DialogView>
    )
}
